/**
 * NodeSettingsFooter Component
 * An inline container at the bottom of a node
 * Contains the settings panel and generate button
 */

import React, { memo, useCallback, useEffect, useState, useRef, forwardRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Slider } from '@mui/material';
import { CreditsIcon } from './CreditsIcon';
import './NodeSettingsFooter.css';

export interface ModelOption {
  id: string;
  name: string;
  description?: string;
}

export interface SettingsFieldConfig {
  id: string;
  label: string;
  type: 'select' | 'toggle' | 'boolean' | 'model' | 'slider' | 'number' | 'anchor';
  options?: Array<{ value: string; label: string }>;
  models?: ModelOption[];
  min?: number;
  max?: number;
  step?: number;
  infoTooltip?: string;
  default?: string | boolean | number;
  hidden?: boolean;
  conditionalOptions?: {
    dependsOn: string;
    rules: Record<string, Array<{ value: string; label: string }>>;
  };
  showWhen?: {
    field: string;
    value?: string | number | boolean;
    operator?: '>' | '<' | '=' | '!=' | '>=' | '<=';
  };
}

export interface NodeSettingsFooterProps {
  /** Node ID to track */
  nodeId: string;
  /** Whether the settings panel is open */
  isSettingsOpen: boolean;
  /** Callback to close settings */
  onSettingsClose: () => void;
  /** If true, hide the close button (for always-visible utility nodes) */
  hideCloseButton?: boolean;
  /** Node type color for panel background */
  color: string;
  /** Field configurations */
  fields: SettingsFieldConfig[];
  /** Current field values */
  values: Record<string, any>;
  /** Callback when a field value changes */
  onChange: (fieldId: string, value: any) => void;
  /** Callback when Generate is clicked */
  onGenerate: () => void;
  /** Callback when Cancel is clicked */
  onCancel?: () => void;
  /** Whether to show Cancel button */
  showCancel?: boolean;
  /** Credit cost for generation */
  creditCost?: number;
  /** When true, a BYOK key covers this node's provider — runs on user's key (no credits) */
  usesOwnKey?: boolean;
  /** Whether generation is in progress */
  isProcessing?: boolean;
  /** Z-index of the parent node (footer will use this + 1) */
  zIndex?: number;
}

/**
 * SettingsField Component
 * Renders a single setting field (dropdown or toggle)
 */
interface SettingsFieldProps {
  config: SettingsFieldConfig;
  value: any;
  onChange: (value: any) => void;
  textColor: string;
  allValues?: Record<string, any>;
  creditCost?: number;
}

const SettingsField: React.FC<SettingsFieldProps> = memo(({ 
  config, 
  value, 
  onChange,
  textColor,
  allValues = {},
  creditCost,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const shouldRender = (() => {
    // Check if field is explicitly hidden
    if (config.hidden) {
      return false;
    }

    if (!config.showWhen) {
      return true;
    }

    const { field, value: condValue, operator = '=' } = config.showWhen;
    const fieldValue = allValues[field];

    switch (operator) {
      case '>':
        return fieldValue > condValue!;
      case '<':
        return fieldValue < condValue!;
      case '>=':
        return fieldValue >= condValue!;
      case '<=':
        return fieldValue <= condValue!;
      case '!=':
        return fieldValue !== condValue;
      case '=':
      default:
        return fieldValue === condValue;
    }
  })();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    setIsDropdownOpen(false);
  }, [onChange]);

  const handleToggle = useCallback(() => {
    onChange(!value);
  }, [onChange, value]);

  // Determine which options to show based on conditional logic
  let displayOptions = config.options || [];
  if (config.conditionalOptions && config.type === 'select') {
    const { dependsOn, rules } = config.conditionalOptions;
    const dependentValue = allValues[dependsOn];
    
    // If there's a rule for the current dependent value, use those options
    if (dependentValue && rules[dependentValue]) {
      displayOptions = rules[dependentValue];
    }
  }

  // Find current option label for select fields
  const currentLabel = config.type === 'select'
    ? displayOptions.find(opt => opt.value === value)?.label || value || 'Select...'
    : config.type === 'model'
    ? config.models?.find(m => m.id === value)?.name || value || 'Select model...'
    : null;

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="nsf-field nodrag">
      {/* Label Row */}
      <div className="nsf-field-label" style={{ color: textColor }}>
        <span>{config.label}</span>
        {config.infoTooltip && (
          <InfoOutlinedIcon 
            className="nsf-field-info" 
            titleAccess={config.infoTooltip}
            style={{ color: textColor }}
          />
        )}
      </div>

      {/* Value Row */}
      {config.type === 'select' || config.type === 'model' ? (
        <div className="nsf-field-dropdown" ref={dropdownRef}>
          <button
            className="nsf-field-dropdown-trigger nodrag"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{ color: textColor }}
          >
            <span className="nsf-field-value">{currentLabel}</span>
            <KeyboardArrowDownIcon 
              className={`nsf-field-chevron ${isDropdownOpen ? 'open' : ''}`}
              style={{ color: textColor }}
            />
          </button>
          
          {isDropdownOpen && (
            <div className="nsf-field-options">
              {config.type === 'model' ? (
                config.models?.map(model => (
                  <button
                    key={model.id}
                    className={`nsf-field-option ${model.id === value ? 'selected' : ''}`}
                    onClick={() => handleSelect(model.id)}
                  >
                    <span className="nsf-option-name">{model.name}</span>
                    {model.description && (
                      <span className="nsf-option-desc">{model.description}</span>
                    )}
                  </button>
                ))
              ) : (
                displayOptions.map(option => (
                  <button
                    key={option.value}
                    className={`nsf-field-option ${option.value === value ? 'selected' : ''}`}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      ) : config.type === 'toggle' || config.type === 'boolean' ? (
        <button
          className={`nsf-toggle ${value ? 'on' : 'off'}`}
          onClick={handleToggle}
          aria-pressed={value}
        >
          <span className="nsf-toggle-knob" />
        </button>
      ) : config.type === 'slider' ? (
        <div className="nsf-slider-container nodrag">
          <Slider
            value={typeof value === 'number' ? value : (config.default as number)}
            onChange={(_, newValue) => onChange(newValue as number)}
            min={config.min ?? 0}
            max={config.max ?? 100}
            step={config.step ?? 1}
            valueLabelDisplay="auto"
            className="nsf-slider nodrag"
            sx={{
              color: textColor,
              height: 4,
              padding: '8px 0',
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: textColor,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: `0 0 0 4px rgba(43, 43, 43, 0.2)`,
                },
              },
              '& .MuiSlider-track': {
                backgroundColor: textColor,
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(43, 43, 43, 0.3)',
              },
              '& .MuiSlider-valueLabel': {
                backgroundColor: textColor,
                color: '#fff',
                fontSize: 11,
              },
            }}
          />
          <span className="nsf-slider-value" style={{ color: textColor }}>{typeof value === 'number' ? value : config.default}</span>
        </div>
      ) : config.type === 'number' ? (
        <div className="nsf-number-container nodrag">
          <button
            className="nsf-number-button nsf-number-decrement"
            onClick={() => {
              const currentValue = typeof value === 'number' ? value : (config.default as number) || 0;
              const newValue = Math.max(config.min ?? -Infinity, currentValue - (config.step ?? 1));
              onChange(newValue);
            }}
          >
            −
          </button>
          <input
            type="number"
            className="nsf-number-input"
            value={typeof value === 'number' ? value : (typeof config.default === 'number' ? config.default : '')}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value);
              if (!isNaN(newValue)) {
                const clampedValue = Math.max(
                  config.min ?? -Infinity,
                  Math.min(config.max ?? Infinity, newValue)
                );
                onChange(clampedValue);
              }
            }}
            min={config.min}
            max={config.max}
            step={config.step ?? 1}
          />
          <button
            className="nsf-number-button nsf-number-increment"
            onClick={() => {
              const currentValue = typeof value === 'number' ? value : (config.default as number) || 0;
              const newValue = Math.min(config.max ?? Infinity, currentValue + (config.step ?? 1));
              onChange(newValue);
            }}
          >
            +
          </button>
          {config.id === 'batchCount' && creditCost !== undefined && creditCost > 0 && (typeof value === 'number' ? value : 1) > 1 && (
            <div className="nsf-batch-cost-label">
              <CreditsIcon size={12} />
              <span>{Math.round(creditCost / (typeof value === 'number' ? value : 1))} each</span>
            </div>
          )}
        </div>
      ) : config.type === 'anchor' ? (
        <div className="nsf-anchor-grid nodrag">
          {[
            ['top-left', 'top-center', 'top-right'],
            ['center-left', 'center', 'center-right'],
            ['bottom-left', 'bottom-center', 'bottom-right'],
          ].map((row, ri) => (
            <div key={ri} className="nsf-anchor-row">
              {row.map((pos) => (
                <button
                  key={pos}
                  className={`nsf-anchor-cell${value === pos ? ' nsf-anchor-active' : ''}`}
                  onClick={() => onChange(pos)}
                  title={pos.replace('-', ' ')}
                />
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});

SettingsField.displayName = 'SettingsField';

/**
 * NodeSettingsFooter Component
 * Renders inline at the bottom of the node
 */
const NodeSettingsFooterComponent = forwardRef<HTMLDivElement, NodeSettingsFooterProps>(({
  nodeId: _nodeId,
  isSettingsOpen,
  onSettingsClose,
  hideCloseButton = false,
  color,
  fields,
  values,
  onChange,
  onGenerate,
  onCancel,
  showCancel = false,
  creditCost,
  usesOwnKey = false,
  isProcessing = false,
  zIndex: _zIndex,
}, ref) => {
  const internalRef = useRef<HTMLDivElement>(null);
  // Use forwarded ref or internal ref
  const containerRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;
  
  // Track closing state for animation
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isSettingsOpen);
  
  // Handle open/close with animation
  useEffect(() => {
    if (isSettingsOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 150); // Match animation duration
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSettingsOpen]);
  
  const textColor = '#2B2B2B';

  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    onChange(fieldId, value);
  }, [onChange]);

  return (
    <div
      ref={containerRef}
      className="node-settings-footer nodrag"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Settings Panel - shown when open or closing (for animation) */}
      {shouldRender && (
        <div 
          className={`nsf-settings-panel ${isClosing ? 'closing' : ''}`}
          style={{ backgroundColor: color }}
        >
          {/* Close Button - hidden for always-visible utility nodes */}
          {!hideCloseButton && (
            <button
              className="nsf-close-button"
              onClick={onSettingsClose}
              aria-label="Close settings"
              style={{ backgroundColor: color }}
            >
              <CloseIcon style={{ fontSize: 16, color: '#2B2B2B' }} />
            </button>
          )}

          {/* Fields */}
          <div className="nsf-fields">
            {fields.map(field => (
              <SettingsField
                key={field.id}
                config={field}
                value={values[field.id] ?? field.default}
                onChange={(value) => handleFieldChange(field.id, value)}
                textColor={textColor}
                allValues={values}
                creditCost={creditCost}
              />
            ))}
          </div>
        </div>
      )}

      {/* Generate Button - always visible */}
      <div className="nsf-generate-container">
        {showCancel && onCancel && (
          <button
            className="nsf-cancel-button nodrag"
            onClick={onCancel}
            disabled={!isProcessing}
          >
            Cancel
          </button>
        )}
        <button
          className="nsf-generate-button nodrag"
          onClick={onGenerate}
          disabled={isProcessing}
        >
          <span>{isProcessing ? 'Generating...' : 'Generate'}</span>
          {creditCost !== undefined && (
            usesOwnKey ? (
              <span className="nsf-generate-credits nsf-generate-byok" title="Runs on your own API key — no credits used">
                <span role="img" aria-label="your key">🔑</span>
                <span className="nsf-credits-strike">{creditCost}</span>
              </span>
            ) : (
              <span className="nsf-generate-credits">
                <CreditsIcon size={12} />
                <span>{creditCost}</span>
              </span>
            )
          )}
        </button>
      </div>
    </div>
  );
});

NodeSettingsFooterComponent.displayName = 'NodeSettingsFooter';

export const NodeSettingsFooter = memo(NodeSettingsFooterComponent);
export default NodeSettingsFooter;
